import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { useRsColors } from '@/hooks/useRsColors';
import { isDateLabel, parseDateLiteral, formatDateLabel, inventoryChangeAxisLabel, isPastThreeYearMark, isWithinThreeYearRange } from '@/utils/chartAxisUtils';
import { getChartFontSize } from '@/utils/chartTypography';
import useChartWindow from '@/hooks/useChartWindow';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import { getDefaultPreviousYearWindowFromLabels } from '@/utils/chartWindow';
import { shouldShowDataLabelsByFrequency } from '@/utils/chartDataLabelPolicy';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface WaterfallChartProps {
  data: number[];
  labels: string[];
  mode: 'total' | 'cumulative';
  className?: string;
  showRangeSlider?: boolean;
  hideDataLabels?: boolean;
}

const WaterfallChart: React.FC<WaterfallChartProps> = ({ data, labels, mode, className, showRangeSlider = true, hideDataLabels = false }) => {
  const rs = useRsColors();
  const toMillions = (value: number) => value / 1_000_000;
  const formatBillions = (value: number) => {
    const abs = Math.abs(value);
    const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return `${Number(value.toFixed(digits))}B`;
  };
  const formatMillionsFromBillions = (value: number) => {
    const inMillions = value * 1000;
    const abs = Math.abs(inMillions);
    const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return `${Number(inMillions.toFixed(digits))}M`;
  };

  const displayData = useMemo(() => {
    if (mode === 'cumulative') {
      let running = 0;
      return data.map((v) => {
        running += v;
        return toMillions(running);
      });
    }
    return data.map((v) => toMillions(v));
  }, [data, mode]);

  const defaultDateWindow = useMemo(
    () => getDefaultPreviousYearWindowFromLabels(labels),
    [labels],
  );

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(labels.length, {
    defaultStartIndex: defaultDateWindow.startIndex,
    defaultEndIndex: defaultDateWindow.endIndex,
  });

  const visibleLabels = useMemo(
    () => (showRangeSlider ? labels.slice(startIndex, endIndex + 1) : labels),
    [showRangeSlider, labels, startIndex, endIndex],
  );
  const visibleData = useMemo(
    () => (showRangeSlider ? displayData.slice(startIndex, endIndex + 1) : displayData),
    [showRangeSlider, displayData, startIndex, endIndex],
  );

  const shouldShowLabels = useMemo(
    () => shouldShowDataLabelsByFrequency({
      enabled: true,
      labels: visibleLabels,
      maxDailyWindowDays: 365,
    }),
    [visibleLabels],
  );

  const barColors = visibleData.map((v) =>
    v >= 0 ? 'rgba(132,204,22,0.85)' : 'rgba(220,38,38,0.9)'
  );

  const chartData = {
    labels: visibleLabels,
    datasets: [
      {
        label: mode === 'cumulative' ? 'Cumulative Change' : 'Monthly Change',
        data: visibleData,
        backgroundColor: barColors,
        borderRadius: 2,
        borderSkipped: false,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20, right: 12, bottom: 8, left: 8 },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
        callbacks: {
          title: (items: any[]) => {
            if (!items.length) return '';
            const raw = items[0].label;
            if (isDateLabel(raw)) return formatDateLabel(parseDateLiteral(raw));
            return raw;
          },
          label: (ctx: any) => {
            const raw = Number(ctx.raw || 0);
            return `${raw >= 0 ? '+' : ''}${formatMillionsFromBillions(raw)} lbs`;
          },
        },
      },
      datalabels: {
        // Hidden below 1024px; otherwise show when the visible window spans ≤ 3 years
        display: (context: any) =>
          !hideDataLabels && shouldShowLabels && isWithinThreeYearRange(context?.chart?.data?.labels ?? []),
        anchor: 'center',
        align: 'center',
        color: rs.offWhite || '#f3f4f6',
        font: { size: getChartFontSize() },
        formatter: (value: number) => {
          if (value === null || value === undefined) return '';
          const formatted = formatMillionsFromBillions(Number(value));
          return value >= 0 ? `+${formatted}` : formatted;
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: (_value: any, index: number) => {
            const lbl = visibleLabels[index];
            if (typeof lbl === 'string' && isDateLabel(lbl)) {
              // <3yr window: every 6 months (MMM yyyy). Once dragged past the
              // 3-year mark, the whole axis switches to year-only.
              const yearOnly = isPastThreeYearMark(visibleLabels);
              return inventoryChangeAxisLabel(parseDateLiteral(lbl), yearOnly) || null;
            }
            return lbl;
          },
        },
        grid: { color: rs.gridLine },
      },
      y: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          callback: (value: any) => formatMillionsFromBillions(Number(value)),
        },
        grid: { color: rs.gridLine },
        title: {
          display: true,
          text: 'Million lbs',
          color: rs.offWhite || '#f3f4f6',
        },
      },
    },
  };

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs]);

  return (
    <div className={className || 'w-full h-full bg-rs-jet rounded'} data-testid="waterfall-chart">
      <div className={showRangeSlider && hasWindowing ? 'h-[calc(100%-50px)] w-full' : 'h-full w-full'}>
        <Bar key={rs.offWhite} data={chartData} options={options} plugins={[hoverLinePlugin]} />
      </div>
      {showRangeSlider && hasWindowing && (
        <ChartRangeSlider
          totalPoints={labels.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onChange={setWindow}
        />
      )}
    </div>
  );
};

export default WaterfallChart;
